import { useState } from 'react';
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import {
  Layers,
  Copy,
  Check,
  Eye,
  EyeOff,
  AlertTriangle,
  Bot,
  Database,
  Shield,
  FileText,
  CheckCircle2,
  Zap,
  Server,
  Mic,
} from 'lucide-react';
import { StackDeploymentInfo } from '@/mock/gpu-instance';

interface StackDeploymentInfoCardProps {
  deploymentInfo: StackDeploymentInfo;
  supportsMIG?: boolean;
}

const getCategoryIcon = (modelId: string) => {
  if (modelId.includes('embedding') || modelId.includes('bert') || modelId.includes('bge') || modelId.includes('sentence'))
    return <Database className="h-3 w-3" />;
  if (modelId.includes('guardrail')) return <Shield className="h-3 w-3" />;
  if (modelId.includes('ocr') || modelId.includes('paddle')) return <FileText className="h-3 w-3" />;
  if (modelId.includes('yolo') || modelId.includes('resnet')) return <Eye className="h-3 w-3" />;
  if (modelId.includes('whisper')) return <Mic className="h-3 w-3" />;
  return <Bot className="h-3 w-3" />;
};

const getFrameworkIcon = (framework: string) => {
  if (framework === 'vllm') return <Zap className="h-4 w-4 text-yellow-500" />;
  if (framework === 'triton') return <Server className="h-4 w-4 text-green-500" />;
  return null;
};

const StackDeploymentInfoCard = ({
  deploymentInfo,
  supportsMIG = true,
}: StackDeploymentInfoCardProps) => {
  const [copiedField, setCopiedField] = useState<string | null>(null);
  const [showApiKey, setShowApiKey] = useState(false);

  const handleCopy = async (text: string, field: string) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopiedField(field);
      setTimeout(() => setCopiedField(null), 2000);
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  };

  const maskApiKey = (key: string) => {
    if (key.length <= 16) return '••••••••••••••••';
    return `${key.slice(0, 10)}${'•'.repeat(16)}${key.slice(-4)}`;
  };

  return (
    <Card className="border-green-500">
      <CardHeader>
        <div className="flex items-center justify-between">
          <CardTitle className="flex items-center gap-2 text-green-600">
            <Layers className="size-5" />
            Stack Deployed Successfully
          </CardTitle>
          <div className="flex items-center gap-2">
            <Badge variant="outline" className="flex items-center gap-1">
              {getFrameworkIcon(deploymentInfo.framework)}
              {deploymentInfo.frameworkName}
            </Badge>
            <Badge variant="secondary" className="bg-green-500/10 text-green-600">
              {deploymentInfo.models.length} Models Ready
            </Badge>
          </div>
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Model Endpoints */}
        <div className="space-y-2">
          <Label className="text-sm text-muted-foreground">Model Endpoints</Label>
          <div className="space-y-2">
            {deploymentInfo.models.map((model) => (
              <div
                key={model.id}
                className="flex items-center justify-between p-3 rounded-lg border bg-muted/30"
              >
                <div className="flex items-center gap-2">
                  <CheckCircle2 className="h-4 w-4 text-green-500" />
                  <div className="flex items-center gap-2">
                    {getCategoryIcon(model.id)}
                    <span className="font-medium text-sm">{model.name}</span>
                  </div>
                  {supportsMIG && (
                    <Badge variant="outline" className="text-xs font-mono">
                      {model.migProfile}
                    </Badge>
                  )}
                </div>
                <Button
                  size="sm"
                  variant="ghost"
                  className="h-7"
                  onClick={() => handleCopy(model.endpoint, model.id)}
                >
                  {copiedField === model.id ? (
                    <Check className="size-3" />
                  ) : (
                    <Copy className="size-3" />
                  )}
                </Button>
              </div>
            ))}
          </div>
        </div>

        {/* Shared API Key */}
        <div className="space-y-2">
          <Label className="text-sm text-muted-foreground">Shared API Key</Label>
          <div className="relative">
            <pre className="bg-muted p-3 rounded-lg text-sm whitespace-pre-wrap break-all pr-20">
              {showApiKey
                ? deploymentInfo.apiKey
                : maskApiKey(deploymentInfo.apiKey)}
            </pre>
            <div className="absolute top-1 right-1 flex gap-1">
              <Button
                size="sm"
                variant="ghost"
                className="h-8"
                onClick={() => setShowApiKey(!showApiKey)}
              >
                {showApiKey ? (
                  <EyeOff className="size-4" />
                ) : (
                  <Eye className="size-4" />
                )}
              </Button>
              <Button
                size="sm"
                variant="ghost"
                className="h-8"
                onClick={() => handleCopy(deploymentInfo.apiKey, 'apiKey')}
              >
                {copiedField === 'apiKey' ? (
                  <Check className="size-4" />
                ) : (
                  <Copy className="size-4" />
                )}
              </Button>
            </div>
          </div>
        </div>

        {/* Usage Example */}
        <div className="space-y-2">
          <Label className="text-sm text-muted-foreground">Example Request</Label>
          <div className="relative">
            <pre className="bg-muted p-3 rounded-lg text-xs whitespace-pre-wrap break-all pr-12 overflow-x-auto font-mono">
{`curl ${deploymentInfo.models[0]?.endpoint || ''} \\
  -H "Content-Type: application/json" \\
  -H "Authorization: Bearer ${deploymentInfo.apiKey}" \\
  -d '{"messages": [{"role": "user", "content": "Hello!"}]}'`}
            </pre>
            <Button
              size="sm"
              variant="ghost"
              className="absolute top-1 right-1 h-8"
              onClick={() =>
                handleCopy(
                  `curl ${deploymentInfo.models[0]?.endpoint || ''} -H "Content-Type: application/json" -H "Authorization: Bearer ${deploymentInfo.apiKey}" -d '{"messages": [{"role": "user", "content": "Hello!"}]}'`,
                  'curl'
                )
              }
            >
              {copiedField === 'curl' ? (
                <Check className="size-4" />
              ) : (
                <Copy className="size-4" />
              )}
            </Button>
          </div>
        </div>

        {/* Demo Warning */}
        <Alert className="border-yellow-500/50 bg-yellow-500/5">
          <AlertTriangle className="h-4 w-4 text-yellow-600" />
          <AlertDescription className="text-yellow-700">
            <span className="font-medium">Demo Mode:</span> This is simulated
            data. In production, all endpoints will be functional with real API
            access.
          </AlertDescription>
        </Alert>
      </CardContent>
    </Card>
  );
};

export default StackDeploymentInfoCard;
